import React, { useEffect, useState } from 'react';
import api, { deleteTransaction, toggleVacation, splitTransaction } from '../api';
import { formatCurrency } from '../utils/formatCurrency';

export default function Transactions({ refreshKey, onUpdated, month }) {

  const [transactions, setTransactions] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [splitTx, setSplitTx] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // { id, category_id, subcategory_id }
  const [editingBudgetDate, setEditingBudgetDate] = useState(null); // { id, value }

  const [filters, setFilters] = useState({
    q: '',
    date_from: '',
    date_to: '',
    min_amount: '',
    max_amount: '',
    type: '',
    is_vacation: false
  });

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== false) params.append(key, value);
      });
      const res = await api.get(`/transactions?${params.toString()}`);
      setTransactions(res.data);
    } catch (err) {
      console.error('Erreur fetch transactions:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette transaction ?")) return;
    try {
      await deleteTransaction(id);
      fetchTransactions();
      onUpdated?.();
    } catch (err) {
      console.error("Erreur suppression :", err);
    }
  };

  const handleToggleVacation = async (id, value) => {
    try {
      await toggleVacation(id, value);
      setTransactions(prev =>
        prev.map(tx => tx.id === id ? { ...tx, is_vacation: value } : tx)
      );
    } catch (err) {
      console.error("Erreur toggle vacation:", err);
    }
  };

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!month) return;
    const [year, monthStr] = month.split("-");
    const firstDay = `${year}-${monthStr}-01`;
    const lastDayNum = new Date(Number(year), Number(monthStr), 0).getDate();
    const lastDay = `${year}-${monthStr}-${String(lastDayNum).padStart(2, "0")}`;
    setFilters(prev => ({ ...prev, date_from: firstDay, date_to: lastDay }));
    setInitialized(true);
  }, [month]);

  useEffect(() => {
    if (!initialized) return;
    fetchTransactions();
  }, [filters, refreshKey, initialized]);

  const resetFilters = () => setFilters({ q: '', date_from: '', date_to: '', min_amount: '', max_amount: '', type: '', is_vacation: false });

  const startEdit = (t) => setEditing({ id: t.id, category_id: t.category_id || '', subcategory_id: t.subcategory_id || '' });

  const saveBudgetDate = async (id, value) => {
    try {
      await api.patch(`/transactions/${id}/budget-date`, { budget_date: value });
      setTransactions(prev => prev.map(t => t.id === id ? { ...t, budget_date: value } : t));
      setEditingBudgetDate(null);
      onUpdated?.();
    } catch (err) {
      console.error('Erreur budget_date:', err);
    }
  };

  const saveEdit = async (subId) => {
    if (!editing) return;
    const catId = editing.category_id || null;
    const subcategoryId = subId || null;
    try {
      await api.patch(`/transactions/${editing.id}/categorize`, { category_id: catId, subcategory_id: subcategoryId });
      const cat = categories.find(c => String(c.id) === String(catId));
      const sub = cat?.subcategories?.find(s => String(s.id) === String(subcategoryId));
      setTransactions(prev => prev.map(t => t.id === editing.id
        ? { ...t, category_id: catId, subcategory_id: subcategoryId, category_name: cat?.name || null, subcategory_name: sub?.name || null }
        : t
      ));
      setEditing(null);
      onUpdated?.();
    } catch (err) {
      console.error('Erreur catégorisation:', err);
    }
  };

  return (
    <div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h2 style={{ margin: 0 }}>Transactions</h2>
        <span style={{ fontSize: 13, color: "var(--text-soft)" }}>
          {transactions.length} transaction{transactions.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* FILTER BAR */}
      <div className="filter-bar">

        <input
          type="text"
          placeholder="Rechercher…"
          value={filters.q}
          onChange={e => setFilters({ ...filters, q: e.target.value })}
          style={{ minWidth: 160 }}
        />

        <input
          type="date"
          value={filters.date_from}
          onChange={e => setFilters({ ...filters, date_from: e.target.value })}
          title="Du"
        />

        <input
          type="date"
          value={filters.date_to}
          onChange={e => setFilters({ ...filters, date_to: e.target.value })}
          title="Au"
        />

        <input
          type="number"
          placeholder="Min €"
          value={filters.min_amount}
          onChange={e => setFilters({ ...filters, min_amount: e.target.value })}
          style={{ width: 80 }}
        />

        <input
          type="number"
          placeholder="Max €"
          value={filters.max_amount}
          onChange={e => setFilters({ ...filters, max_amount: e.target.value })}
          style={{ width: 80 }}
        />

        <select
          value={filters.type}
          onChange={e => setFilters({ ...filters, type: e.target.value })}
          style={{ width: 120 }}
        >
          <option value="">Tout type</option>
          <option value="expense">Dépenses</option>
          <option value="income">Revenus</option>
        </select>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text-soft)", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={filters.is_vacation}
            onChange={e => setFilters({ ...filters, is_vacation: e.target.checked })}
          />
          Vacances
        </label>

        <button className="btn btn-ghost" onClick={resetFilters} style={{ marginLeft: "auto" }}>
          Réinitialiser
        </button>

      </div>

      {/* TABLE */}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Date <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-muted)' }}>(budget)</span></th>
              <th>Libellé</th>
              <th>Marchand</th>
              <th>Catégorie</th>
              <th>Sous-catégorie</th>
              <th style={{ textAlign: 'right' }}>Montant</th>
              <th style={{ textAlign: 'center' }}>🏖</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>

          <tbody>
            {transactions.length === 0 && (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', color: "var(--text-muted)", padding: "32px 14px" }}>
                  Aucune transaction
                </td>
              </tr>
            )}

            {transactions.map(t => (
              <tr key={t.id} style={{ background: t.is_vacation ? "#f0f9ff" : undefined }}>

                <td style={{ color: "var(--text-soft)", whiteSpace: "nowrap" }}>
                  <div>{new Date(t.date).toLocaleDateString('fr-FR')}</div>
                  {editingBudgetDate?.id === t.id ? (
                    <input
                      type="date"
                      autoFocus
                      value={editingBudgetDate.value}
                      onChange={e => setEditingBudgetDate(prev => ({ ...prev, value: e.target.value }))}
                      onBlur={e => saveBudgetDate(t.id, e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveBudgetDate(t.id, editingBudgetDate.value);
                        if (e.key === 'Escape') setEditingBudgetDate(null);
                      }}
                      style={{ fontSize: 11, padding: '1px 4px', borderRadius: 4, border: '1px solid var(--primary)', marginTop: 2 }}
                    />
                  ) : (
                    t.budget_date && t.budget_date.slice(0, 10) !== t.date.slice(0, 10) && (
                      <div
                        onClick={() => setEditingBudgetDate({ id: t.id, value: t.budget_date.slice(0, 10) })}
                        title="Date budget (cliquer pour modifier)"
                        style={{ fontSize: 11, color: 'var(--primary)', cursor: 'pointer', marginTop: 2 }}
                      >
                        📅 {new Date(t.budget_date).toLocaleDateString('fr-FR')}
                      </div>
                    ) || (
                      <div
                        onClick={() => setEditingBudgetDate({ id: t.id, value: (t.budget_date || t.date).slice(0, 10) })}
                        title="Modifier la date budget"
                        style={{ fontSize: 10, color: 'var(--text-muted)', cursor: 'pointer', marginTop: 1, opacity: 0.5 }}
                      >
                        ✎
                      </div>
                    )
                  )}
                </td>

                <td style={{ wordBreak: "break-word" }}>
                  {t.raw_label || '—'}
                  {t.source && (
                    <span style={{
                      marginLeft: 6,
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: 10,
                      verticalAlign: 'middle',
                      background: t.source === 'import' ? '#f1f5f9' : t.source === 'enable_banking' ? '#f0fdf4' : '#eff6ff',
                      color:      t.source === 'import' ? '#64748b'  : t.source === 'enable_banking' ? '#16a34a' : '#3b82f6',
                      border:     `1px solid ${t.source === 'import' ? '#cbd5e1' : t.source === 'enable_banking' ? '#86efac' : '#93c5fd'}`,
                    }}>
                      {t.source === 'import' ? 'CSV' : t.source === 'enable_banking' ? 'Banque' : 'Manuel'}
                    </span>
                  )}
                </td>

                <td style={{ color: "var(--text-soft)" }}>{t.merchant || '—'}</td>

                {editing?.id === t.id ? (
                  <>
                    <td>
                      <select
                        autoFocus
                        value={editing.category_id}
                        onChange={e => setEditing(prev => ({ ...prev, category_id: e.target.value, subcategory_id: '' }))}
                        onKeyDown={e => e.key === 'Escape' && setEditing(null)}
                        style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid var(--primary)', fontSize: 12, maxWidth: 130 }}
                      >
                        <option value="">—</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </td>
                    <td>
                      <select
                        value={editing.subcategory_id}
                        onChange={e => saveEdit(e.target.value)}
                        onKeyDown={e => e.key === 'Escape' && setEditing(null)}
                        disabled={!editing.category_id}
                        style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid var(--primary)', fontSize: 12, maxWidth: 150 }}
                      >
                        <option value="">—</option>
                        {categories.find(c => String(c.id) === String(editing.category_id))?.subcategories?.map(s =>
                          <option key={s.id} value={s.id}>{s.name}</option>
                        )}
                      </select>
                    </td>
                  </>
                ) : (
                  <>
                    <td onClick={() => startEdit(t)} style={{ cursor: 'pointer' }} title="Cliquer pour modifier">
                      {t.category_name
                        ? <span style={{ padding: "2px 8px", background: "var(--border-soft)", borderRadius: 99, fontSize: 12 }}>{t.category_name}</span>
                        : <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>
                      }
                    </td>
                    <td onClick={() => startEdit(t)} style={{ color: "var(--text-soft)", cursor: 'pointer' }} title="Cliquer pour modifier">
                      {t.subcategory_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                  </>
                )}

                <td style={{ textAlign: 'right', fontWeight: 600, whiteSpace: "nowrap" }}>
                  <span style={{ color: t.type === 'expense' ? 'var(--danger)' : 'var(--success)' }}>
                    {t.type === 'expense' ? '−' : '+'}{formatCurrency(Math.abs(t.amount))}
                  </span>
                </td>

                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={t.is_vacation || false}
                    onChange={e => handleToggleVacation(t.id, e.target.checked)}
                  />
                </td>

                <td style={{ display: 'flex', gap: 4 }}>
                  <button
                    className="btn btn-ghost"
                    onClick={() => setSplitTx(t)}
                    style={{ padding: "4px 8px", fontSize: 13 }}
                    title="Éclater"
                  >
                    ✂
                  </button>
                  <button
                    className="btn btn-ghost"
                    onClick={() => handleDelete(t.id)}
                    style={{ padding: "4px 8px", fontSize: 14 }}
                    title="Supprimer"
                  >
                    🗑
                  </button>
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {splitTx && (
        <SplitModal
          tx={splitTx}
          categories={categories}
          onClose={() => setSplitTx(null)}
          onDone={() => { setSplitTx(null); fetchTransactions(); onUpdated?.(); }}
        />
      )}

    </div>
  );
}

function SplitModal({ tx, categories, onClose, onDone }) {
  const total = Math.abs(Number(tx.amount));
  const [lines, setLines] = useState([
    { amount: '', category_id: tx.category_id || '', subcategory_id: tx.subcategory_id || '' },
    { amount: '', category_id: '', subcategory_id: '' },
  ]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const sumLines = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const remaining = Math.round((total - sumLines) * 100) / 100;

  const setLine = (i, field, value) => {
    setLines(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      if (field === 'category_id') next[i].subcategory_id = '';
      return next;
    });
  };

  const addLine = () => setLines(prev => [...prev, { amount: '', category_id: '', subcategory_id: '' }]);
  const removeLine = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (Math.abs(remaining) > 0.01) { setError(`Reste ${remaining.toFixed(2)} € à répartir`); return; }
    if (lines.some(l => !l.amount || parseFloat(l.amount) <= 0)) { setError('Tous les montants doivent être > 0'); return; }
    setSaving(true);
    try {
      await splitTransaction(tx.id, lines.map(l => ({
        amount: parseFloat(l.amount),
        category_id: l.category_id || null,
        subcategory_id: l.subcategory_id || null,
      })));
      onDone();
    } catch (err) {
      setError(err?.response?.data?.error || 'Erreur');
      setSaving(false);
    }
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
  };
  const boxStyle = {
    background: 'var(--card)', borderRadius: 'var(--radius-lg)', padding: '28px 32px',
    width: 560, maxWidth: '95vw', boxShadow: 'var(--shadow-md)'
  };

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={boxStyle}>
        <h3 style={{ margin: '0 0 4px' }}>Éclater la transaction</h3>
        <div style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 20 }}>
          {tx.raw_label || tx.merchant} — {formatCurrency(total)}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
          <thead>
            <tr style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              <th style={{ textAlign: 'left', paddingBottom: 6, width: 90 }}>Montant</th>
              <th style={{ textAlign: 'left', paddingBottom: 6 }}>Catégorie</th>
              <th style={{ textAlign: 'left', paddingBottom: 6 }}>Sous-catégorie</th>
              <th style={{ width: 32 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => {
              const cat = categories.find(c => String(c.id) === String(line.category_id));
              return (
                <tr key={i}>
                  <td style={{ paddingBottom: 8, paddingRight: 8 }}>
                    <input
                      type="number" min="0" step="0.01"
                      value={line.amount}
                      onChange={e => setLine(i, 'amount', e.target.value)}
                      style={{ width: 80, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
                      placeholder="0.00"
                    />
                  </td>
                  <td style={{ paddingBottom: 8, paddingRight: 8 }}>
                    <select
                      value={line.category_id}
                      onChange={e => setLine(i, 'category_id', e.target.value)}
                      style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
                    >
                      <option value="">—</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </td>
                  <td style={{ paddingBottom: 8, paddingRight: 8 }}>
                    <select
                      value={line.subcategory_id}
                      onChange={e => setLine(i, 'subcategory_id', e.target.value)}
                      disabled={!cat}
                      style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
                    >
                      <option value="">—</option>
                      {cat?.subcategories?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </td>
                  <td style={{ paddingBottom: 8 }}>
                    {lines.length > 2 && (
                      <button onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16 }}>✕</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <button onClick={addLine} style={{ fontSize: 13, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 16 }}>
          + Ajouter une ligne
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: Math.abs(remaining) < 0.01 ? 'var(--success)' : 'var(--warning)' }}>
            Reste : {formatCurrency(remaining)}
          </span>
          {error && <span style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</span>}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || Math.abs(remaining) > 0.01}
          >
            {saving ? 'Enregistrement…' : 'Éclater'}
          </button>
        </div>
      </div>
    </div>
  );
}
